import { Button, Form } from 'antd';
import React, { useEffect } from 'react';
import FilterDropdown from './Dropdown';
import { Option } from 'app/types/interfaces/Option';
import classes from './Filterbar.module.scss';
import Sorter from './Sorter';
import set from 'lodash/set';
import { isEmpty, isEqual } from 'lodash';
import { RangeValue } from 'rc-picker/lib/interface';

export enum EFilterItemType {
  'DROPDOWN' = 'dropdown',
  'SORT' = 'sort',
  'SORT_DATE' = 'sort_date'
}

type FilterItemType = {
  key: string;
  options: Option[];
  icon?: React.ReactNode;
  label: React.ReactNode | ((selected: any[], extra: any) => React.ReactNode);
  type: EFilterItemType.DROPDOWN;
  haveOptionAll?: boolean;
};

type FilterItemSortType = {
  key: string;
  icon?: React.ReactNode;
  label:
    | React.ReactNode
    | ((
        selected: any[],
        sortBy: string,
        range: RangeValue<moment.Moment>,
        config: Props
      ) => React.ReactNode);
  type: EFilterItemType.SORT | EFilterItemType.SORT_DATE;
  options?: Option[]; // Add the 'options' property here
  sortByOptions?: Option[];
};

type FilterItem = FilterItemType | FilterItemSortType;

type Props = {
  filterItems: Array<FilterItem>;
  value?: {
    [key: string]: any;
  };
  defaultValue?: { [key: string]: any };
  onChange?: (
    filter: { [key: string]: any },
    sort: {
      [key: string]: any;
    }
  ) => void;
};

export default function FilterBar({
  filterItems,
  onChange,
  value = {},
  defaultValue = {}
}: Props) {
  const [form] = Form.useForm();

  const parseValueWithFilterItem = (values) => {
    const params = filterItems.reduce(
      (
        acc: {
          [key: string]: any;
        },
        filterItem
      ) => {
        if (filterItem.type === EFilterItemType.DROPDOWN) {
          set(acc, `filter.${filterItem.key}`, values[filterItem.key]);
        }
        if (
          filterItem.type === EFilterItemType.SORT ||
          filterItem.type === EFilterItemType.SORT_DATE
        ) {
          set(acc, `sort.${filterItem.key}`, values[filterItem.key]);
        }
        return acc;
      },
      {
        filter: {},
        sort: {}
      }
    );

    return params;
  };

  const isFormValueEmpty = (values) => {
    if (defaultValue) {
      return isEqual(defaultValue, values);
    }

    return Object.keys(values).every((key) => {
      const itemValue = values[key];
      if (Array.isArray(itemValue)) {
        if (itemValue.indexOf('all') > -1) {
          return true;
        }
        return isEmpty(itemValue);
      }

      return isEmpty(itemValue);
    });
  };

  useEffect(() => {
    if (value) {
      form.setFieldsValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  return (
    <Form
      form={form}
      initialValues={{
        ...defaultValue
      }}
      onValuesChange={(_, values) => {
        if (onChange) {
          const { filter, sort } = parseValueWithFilterItem(values);
          onChange(filter, sort);
        }
      }}
    >
      <div className={classes.filterBar}>
        <div className={classes.left}>
          {filterItems.map((filterItem) => {
            switch (filterItem.type) {
              case EFilterItemType.DROPDOWN: {
                return (
                  <Form.Item key={filterItem.key} name={filterItem.key}>
                    <FilterDropdown
                      label={filterItem.label}
                      icon={filterItem.icon}
                      options={filterItem.options}
                      haveOptionAll={filterItem.haveOptionAll}
                    ></FilterDropdown>
                  </Form.Item>
                );
              }
              case EFilterItemType.SORT:
              case EFilterItemType.SORT_DATE: {
                return (
                  <Form.Item key={filterItem.key} name={filterItem.key}>
                    <Sorter
                      label={filterItem.label as any}
                      icon={filterItem.icon}
                      options={filterItem.options}
                      sortByOptions={filterItem.sortByOptions}
                      allowRangePicker={
                        filterItem.type === EFilterItemType.SORT_DATE
                      }
                    ></Sorter>
                  </Form.Item>
                );
              }
              default:
                return null;
            }
          })}
        </div>
        <div className={classes.right}>
          <Form.Item noStyle shouldUpdate>
            {(instance) => {
              return (
                <Button
                  type="link"
                  disabled={isFormValueEmpty(instance.getFieldsValue())}
                  onClick={() => {
                    instance.setFieldsValue(defaultValue || {});
                    instance.resetFields();
                  }}
                >
                  Reset
                </Button>
              );
            }}
          </Form.Item>
        </div>
      </div>
    </Form>
  );
}
